import { NETWORK_ERROR, COMMON_SERVICE_ERROR } from '../model/errors';

/** Set on errors rejected from shared axios interceptors (global unhandledrejection failsafe). */
export const DDV_AXIOS_REST_FAILURE = Symbol.for('ddv.fromAxiosInterceptor');

/**
 * Normalized client-side error for failed REST calls (axios error path).
 */
export class ApiClientError extends Error {
  /**
   * @param {object} opts
   * @param {string} opts.message
   * @param {number} [opts.httpStatus]
   * @param {number|string} [opts.apiStatusCode] from JSON body when present
   * @param {string} [opts.requestId]
   * @param {string} [opts.url]
   * @param {*} [opts.rawPayload]
   * @param {string} [opts.code] axios / network code (e.g. ERR_NETWORK)
   */
  constructor({
    message,
    httpStatus,
    apiStatusCode,
    requestId,
    url,
    rawPayload,
    code,
  }) {
    super(message);
    this.name = 'ApiClientError';
    this.httpStatus = httpStatus;
    this.apiStatusCode = apiStatusCode;
    this.requestId = requestId;
    this.url = url;
    this.rawPayload = rawPayload;
    this.code = code;
  }
}

const includeRawPayload = () => process.env.NODE_ENV === 'development';

function getRequestMeta(axiosError) {
  const cfg = axiosError?.config || {};
  const headers = cfg.headers || {};
  const requestId =
    headers.requestId ||
    headers['request-id'] ||
    headers['Request-Id'] ||
    undefined;
  return {
    url: cfg.url || '',
    requestId,
  };
}

function parseNewContract(data, response, meta) {
  if (
    data &&
    data.success === false &&
    typeof data.message === 'string' &&
    data.message.length
  ) {
    return new ApiClientError({
      message: data.message,
      httpStatus: response?.status,
      apiStatusCode: data.statusCode,
      requestId: meta.requestId,
      url: meta.url,
      rawPayload: includeRawPayload() ? data : undefined,
    });
  }
  return null;
}

function parseLegacyBody(data, response, axiosError, meta) {
  // Match legacy axiosService ordering (each branch can override the previous).
  let message = NETWORK_ERROR.INTERNAL_SERVER_ERROR;

  if (data?.error?.includes?.('has duplicate')) {
    message = COMMON_SERVICE_ERROR.DUPLICATE;
  }
  if (data?.error?.message === 'Invoking-IAM-Failure') {
    message = NETWORK_ERROR.SAVE_PROJECT_SETTING_ERROR;
  }
  if (data?.error?.message === 'Invite-User-Failure') {
    message = NETWORK_ERROR.SAVE_CLIENT_ERROR;
  }
  if (data?.errMsg) {
    message = data.errMsg;
  }
  if (data?.data?.data?.error?.message) {
    message = data.data.data.error.message;
  }
  if (axiosError?.code === NETWORK_ERROR.ERR_BAD_RESPONSE) {
    message = data?.data?.data?.message || axiosError.message || message;
  }

  return new ApiClientError({
    message: typeof message === 'string' ? message : String(message),
    httpStatus: response?.status,
    apiStatusCode: data?.statusCode,
    requestId: meta.requestId,
    url: meta.url,
    rawPayload: includeRawPayload() ? data : undefined,
    code: axiosError?.code,
  });
}

/**
 * Map an axios error to ApiClientError (new API shape first, then legacy fallbacks).
 * @param {import('axios').AxiosError} axiosError
 * @returns {ApiClientError}
 */
export function parseRestApiError(axiosError) {
  const response = axiosError?.response;
  const data = response?.data;
  const meta = getRequestMeta(axiosError);

  const fromNew = parseNewContract(data, response, meta);
  if (fromNew) {
    return fromNew;
  }

  if (!response) {
    return new ApiClientError({
      message: axiosError?.message || NETWORK_ERROR.INTERNAL_SERVER_ERROR,
      httpStatus: undefined,
      apiStatusCode: undefined,
      requestId: meta.requestId,
      url: meta.url,
      rawPayload: includeRawPayload() ? axiosError : undefined,
      code: axiosError?.code,
    });
  }

  return parseLegacyBody(data, response, axiosError, meta);
}

/**
 * Mark an error as originating from axiosService error handling (for window-level failsafe).
 * @param {ApiClientError} err
 * @returns {ApiClientError}
 */
export function markAsAxiosRestFailure(err) {
  if (err && typeof err === 'object') {
    Object.defineProperty(err, DDV_AXIOS_REST_FAILURE, {
      value: true,
      enumerable: false,
      configurable: true,
    });
  }
  return err;
}

export function isAxiosRestFailure(reason) {
  return Boolean(
    reason &&
      typeof reason === 'object' &&
      reason[DDV_AXIOS_REST_FAILURE] === true &&
      (reason instanceof ApiClientError || reason?.name === 'ApiClientError'),
  );
}
